import {
  DownloadOutlined,
  FileTextOutlined,
  HeartOutlined,
  LockOutlined,
  MailOutlined,
  SaveOutlined,
  VideoCameraOutlined
} from '@ant-design/icons';
import { Button, Flex, message as antMessage } from 'antd';
import { DesignProps } from 'api/DesignApi';
import {
  ErrorResponseType,
  MessageTypeEnum,
  useGetFileMessagePublicQuery,
  useGetOneMessagePublicQuery
} from 'api/MessagesApi';
import DownloadLink from 'components/Message/DownloadLink/DownloadLink';
import MessageIcon from 'components/Message/MessageIcon';
import SpinComponent from 'components/Tool/SpinComponent/SpinComponent';
import { AppAudioPlayer } from 'components/UiKit/Media/AppAudioPlayer';
import { TextToHtmlComponent } from 'components/UiKit/TextToHtmlComponent/TextToHtmlComponent';
import { useCompanyContext } from 'contexts/CompanyContext';
import type { CSSProperties } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { selectAuthUser } from 'store/authSlice';
import { buildBrandedInviteSignupHref } from 'utils/brandedInviteSignup';
import { isEternalOnlyBranding, toAlphaColor } from 'utils/branding';
import { ensureHttp, getFullNameOrEmail, isAudioFile, isVideoFile } from 'utils/utils';
import './PublicMessage.css';

const formatDateLabel = (dateValue?: string) => {
  if (!dateValue) {
    return undefined;
  }

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) {
    return undefined;
  }

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

export default function PublicMessage({
  messageContactId,
  designProps
}: {
  messageContactId: string;
  designProps?: DesignProps;
}) {
  const user = useSelector(selectAuthUser);
  const { companyConfig } = useCompanyContext();
  const navigate = useNavigate();
  const location = useLocation();

  const { data, isLoading, error } = useGetOneMessagePublicQuery({ messageContactId });
  const message = data?.message;
  const eternalPrimaryColor = '#5449dc';
  const eternalPrimaryColorLighter = '#eeedfb';
  const partnerName = designProps?.companyName?.trim() || 'Eternal';
  const useEternalOnlyBranding = isEternalOnlyBranding(partnerName);
  const primaryColor = useEternalOnlyBranding ? eternalPrimaryColor : designProps?.colorPrimary || eternalPrimaryColor;
  const lighterColor = useEternalOnlyBranding
    ? eternalPrimaryColorLighter
    : designProps?.colorPrimaryLighter || eternalPrimaryColorLighter;
  const userSenderIdentity = message?.user ? getFullNameOrEmail(message.user, '') : '';
  const patientProfileSenderIdentity = message?.patientProfile
    ? `${message.patientProfile.firstName || ''} ${message.patientProfile.lastName || ''}`.trim() ||
      message.patientProfile.email ||
      ''
    : '';
  const senderIdentity = userSenderIdentity.trim() || patientProfileSenderIdentity.trim() || '';
  const hasSender = !!senderIdentity;
  const normalizedMessageType =
    message?.type === MessageTypeEnum.Video
      ? 'Video message'
      : message?.type === MessageTypeEnum.Audio
        ? 'Audio message'
        : 'Message';
  const recordedDate = formatDateLabel(
    (message as { recordedAt?: string; createdAt?: string } | undefined)?.recordedAt ||
      (message as { recordedAt?: string; createdAt?: string } | undefined)?.createdAt
  );
  const supportHref = `mailto:${designProps?.email || companyConfig.contactEmail}`;
  const eternalHref = designProps?.domainUrl ? ensureHttp(designProps.domainUrl) : '/';
  const signupHref = buildBrandedInviteSignupHref(
    location,
    designProps?.domainUrl || companyConfig.domainUrl || process.env.REACT_APP_MAIN_WEBSITE_URL
  );

  const handleSignupNavigation = () => {
    if (/^https?:\/\//i.test(signupHref)) {
      window.location.assign(signupHref);
      return;
    }

    navigate(signupHref);
  };

  const { data: messageFile, isLoading: isLoadingFile } = useGetFileMessagePublicQuery({
    messageContactId
  });
  const resolvedFileName = messageFile?.fileName || message?.fileName;

  const errorStatus = (error as ErrorResponseType | undefined)?.status;
  const isNotFound = !messageContactId || errorStatus === 404;
  const isExpired = errorStatus === 403;
  const hasUnhandledError = !!error && !isExpired && !isNotFound;

  if (!isLoading && hasUnhandledError) {
    return (
      <div className="public-message">
        <div className="public-message-layout">
          <div className="message-error-card">
            <div className="message-error-title">We couldn&apos;t load this message</div>
            <div className="message-error-subtitle">
              Something went wrong on our side. Please try again in a moment or contact support.
            </div>
            <div className="message-error-actions">
              <a
                href={supportHref}
                target={supportHref.startsWith('mailto:') ? undefined : '_blank'}
                rel={supportHref.startsWith('mailto:') ? undefined : 'noopener noreferrer'}
                className="support-link"
              >
                Contact support
              </a>
              <a
                href={eternalHref}
                target={eternalHref === '/' ? undefined : '_blank'}
                rel={eternalHref === '/' ? undefined : 'noopener noreferrer'}
                className="support-link"
              >
                {useEternalOnlyBranding ? 'Go to Eternal' : `Go to ${partnerName}`}
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isLoading && (isExpired || isNotFound)) {
    return (
      <div className="public-message">
        <div className="public-message-layout">
          <div className="message-error-card">
            <div className="message-error-title">We couldn&apos;t find this message</div>
            <div className="message-error-subtitle">
              This link may have expired, been removed, or is temporarily unavailable.
            </div>
            <div className="message-error-actions">
              <a
                href={supportHref}
                target={supportHref.startsWith('mailto:') ? undefined : '_blank'}
                rel={supportHref.startsWith('mailto:') ? undefined : 'noopener noreferrer'}
                className="support-link"
              >
                Contact support
              </a>
              <a
                href={eternalHref}
                target={eternalHref === '/' ? undefined : '_blank'}
                rel={eternalHref === '/' ? undefined : 'noopener noreferrer'}
                className="support-link"
              >
                {useEternalOnlyBranding ? 'Go to Eternal' : `Go to ${partnerName}`}
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (!message) {
    return (
      <div className="public-message">
        <div className="public-message-layout">
          <SpinComponent isLoading={isLoading || isLoadingFile}>
            <div />
          </SpinComponent>
        </div>
      </div>
    );
  }

  const showDownloadError = () => {
    antMessage.error('We couldn’t download this message. Please try again or contact support.');
  };

  const isUploadedVideoMessage =
    message.type === MessageTypeEnum.Video && !!resolvedFileName && isVideoFile(resolvedFileName);
  const canDownloadVideoMessage = !!messageFile?.signedUrl;
  const isUploadedWrittenMessage =
    message.type === MessageTypeEnum.Text &&
    !!resolvedFileName &&
    !isAudioFile(resolvedFileName) &&
    !isVideoFile(resolvedFileName);
  const canDownloadWrittenMessage = !!messageFile?.signedUrl;

  const renderMedia = () => {
    const fallback = (
      <div className="media-fallback-thumbnail">
        <div className="media-fallback-overlay">
          <div className="media-fallback-label">Preview unavailable • download to view</div>
        </div>
      </div>
    );

    if (resolvedFileName && message.type === MessageTypeEnum.Audio && isAudioFile(resolvedFileName)) {
      return (
        <div className="public-message-player">
          <AppAudioPlayer url={messageFile?.signedUrl} fileName={resolvedFileName} fallback={fallback} />
        </div>
      );
    }

    return fallback;
  };

  return (
    <div className="public-message">
      <div
        className="public-message-layout"
        style={
          {
            '--color-primary': primaryColor,
            '--color-primary-lighter': lighterColor
          } as CSSProperties
        }
      >
        <div className="message-hero-card">
          <div
            className="message-hero-icon"
            style={{
              backgroundColor: primaryColor,
              color: '#fff'
            }}
          >
            <MailOutlined />
          </div>
          <div className="message-hero-copy">
            <div className="message-hero-title">You have a new message</div>
            {!useEternalOnlyBranding && <div className="message-hero-subtitle">Shared with care by {partnerName}</div>}
            <div className="message-hero-subtitle">
              {hasSender ? `From ${senderIdentity}` : 'Someone shared a message with you'}
            </div>
          </div>
        </div>

        <div className="message-card">
          <SpinComponent isLoading={isLoading || isLoadingFile}>
            <Flex align="center" gap={12} className="message-card-header">
              <div
                className="message-card-icon"
                style={{
                  backgroundColor: primaryColor,
                  color: '#fff'
                }}
              >
                {message.type === MessageTypeEnum.Video ? (
                  <VideoCameraOutlined />
                ) : message.type === MessageTypeEnum.Text ? (
                  <FileTextOutlined />
                ) : (
                  <MessageIcon type={message.type} />
                )}
              </div>
              <Flex vertical={true} gap={4}>
                <div className="message-card-title">
                  {normalizedMessageType === 'Message' ? 'Message' : normalizedMessageType}
                </div>
                <div className="message-card-meta">Recorded in app{recordedDate ? ` • ${recordedDate}` : ''}</div>
              </Flex>
            </Flex>

            <div className="message-support-copy">
              A message has been shared with you. Take a moment whenever you&apos;re ready.
            </div>

            <div className="message-media-wrapper">
              {isUploadedVideoMessage ? (
                <div
                  className="video-download-panel public-message-player"
                  style={{
                    borderColor: toAlphaColor(primaryColor, 0.22),
                    backgroundColor: toAlphaColor(primaryColor, 0.04)
                  }}
                >
                  <div className="video-download-panel-icon" style={{ color: '#fff', backgroundColor: primaryColor }}>
                    <VideoCameraOutlined />
                  </div>
                  <div className="video-download-panel-text">
                    {canDownloadVideoMessage
                      ? 'This video message is available to download and view on your device.'
                      : 'This video message is not available to download right now. Please contact support.'}
                  </div>
                  <DownloadLink
                    message={message}
                    messageFile={messageFile}
                    designProps={designProps}
                    disabled={!canDownloadVideoMessage}
                    onError={showDownloadError}
                    render={({ onClick, disabled }) => (
                      <Button
                        type="primary"
                        className="video-download-panel-button"
                        icon={<DownloadOutlined />}
                        style={{ backgroundColor: primaryColor, borderColor: primaryColor }}
                        disabled={disabled}
                        onClick={onClick}
                      >
                        Download to view message
                      </Button>
                    )}
                  />
                </div>
              ) : isUploadedWrittenMessage ? (
                <div
                  className="video-download-panel public-message-player"
                  style={{
                    borderColor: toAlphaColor(primaryColor, 0.22),
                    backgroundColor: toAlphaColor(primaryColor, 0.04)
                  }}
                >
                  <div className="video-download-panel-icon" style={{ color: '#fff', backgroundColor: primaryColor }}>
                    <FileTextOutlined />
                  </div>
                  <div className="video-download-panel-text">
                    {canDownloadWrittenMessage
                      ? 'This written message is available to download and view on your device.'
                      : 'This written message is not available to download right now. Please contact support.'}
                  </div>
                  <DownloadLink
                    message={message}
                    messageFile={messageFile}
                    designProps={designProps}
                    disabled={!canDownloadWrittenMessage}
                    onError={showDownloadError}
                    render={({ onClick, disabled }) => (
                      <Button
                        type="primary"
                        className="video-download-panel-button"
                        icon={<DownloadOutlined />}
                        style={{ backgroundColor: primaryColor, borderColor: primaryColor }}
                        disabled={disabled}
                        onClick={onClick}
                      >
                        Download file
                      </Button>
                    )}
                  />
                </div>
              ) : message.type === MessageTypeEnum.Text ? (
                <div className="message-text-box public-message-player">
                  <TextToHtmlComponent text={message.content} />
                </div>
              ) : resolvedFileName ? (
                isAudioFile(resolvedFileName) || isVideoFile(resolvedFileName) ? (
                  renderMedia()
                ) : (
                  <div className="message-text-box public-message-player">
                    <Flex align="center" gap={8}>
                      <FileTextOutlined />
                      <DownloadLink
                        message={message}
                        messageFile={messageFile}
                        designProps={designProps}
                        text={resolvedFileName}
                        onError={showDownloadError}
                        disabled={!messageFile?.signedUrl}
                      />
                    </Flex>
                  </div>
                )
              ) : null}
            </div>

            <div className="message-action-row">
              {!isUploadedVideoMessage && !isUploadedWrittenMessage && (
                <DownloadLink
                  message={message}
                  messageFile={messageFile}
                  designProps={designProps}
                  disabled={message.type !== MessageTypeEnum.Text && !messageFile?.signedUrl}
                  onError={showDownloadError}
                  render={({ onClick, disabled }) => (
                    <Button
                      className="message-secondary-button"
                      icon={<DownloadOutlined />}
                      disabled={disabled}
                      onClick={onClick}
                    >
                      Download
                    </Button>
                  )}
                />
              )}

              {!user && (
                <Button
                  className="message-secondary-button"
                  icon={<SaveOutlined />}
                  style={{ borderColor: primaryColor, color: primaryColor }}
                  onClick={handleSignupNavigation}
                >
                  Save to my account
                </Button>
              )}
            </div>
          </SpinComponent>
        </div>

        <div className="keep-message-card">
          <div className="keep-message-content-wrap">
            <HeartOutlined
              className="keep-message-icon"
              style={{
                backgroundColor: primaryColor,
                color: '#fff'
              }}
            />
            <div className="keep-message-copy">
              <div className="keep-message-title">
                {user ? 'This message is saved in your Eternal inbox' : 'Keep this message forever'}
              </div>
              <div className="keep-message-subtitle">
                {user
                  ? 'Come back to it anytime — revisit it, keep it with the rest of your memories.'
                  : 'Create a free Eternal account to save this message to your personal inbox — so you can revisit it anytime, alongside all your other memories.'}
              </div>
            </div>
          </div>
          {!user && (
            <Button
              className="keep-message-button"
              type="primary"
              style={{ backgroundColor: primaryColor, borderColor: primaryColor }}
              onClick={handleSignupNavigation}
            >
              Create free account
            </Button>
          )}
        </div>

        <div className="trust-row">
          <div className="trust-copy">
            <LockOutlined />
            {useEternalOnlyBranding
              ? 'Securely delivered with care.'
              : `Securely delivered with care by ${partnerName} and Eternal.`}
          </div>
        </div>
      </div>
    </div>
  );
}
